import axios from "axios";

import { CHAIN_ID } from "@/constants/chains";
import {
  API_ROUTES,
  API_ROUTES_V2,
  AirdropSignatureResponse,
  BasicResponse,
  BasicResponseV2,
  BasicResponseV2Auth,
  BasicResponseV2Score,
  ErrorAction,
  GraphResponse,
  MintResponse,
  RiskResponse,
  TotalScoreResponse,
  TrustResponse,
  UserResponse,
  XResponse,
} from "@/interfaces/api";
import { Network, NetworkChainMap } from "@/interfaces/network";
import { ApiVersion, apiVersion } from "@/utils";

import { config } from "../config";

export const localStorageKey = "hapi-app-auth-token";

export const INTERNAL_CHAIN_BY_NETWORK: Record<Network, CHAIN_ID | null> = {
  [Network.NEAR]: -1,
  [Network.TON]: -239,
  [Network.TON_TESTNET]: null,
  [Network.SOLANA]: -2,
  [Network.BASE]: CHAIN_ID.BASE,
  [Network.LINEA]: CHAIN_ID.LINEA,
  [Network.BSC]: CHAIN_ID.BSC,
  [Network.ARBITRUM]: CHAIN_ID.ARBITRUM,
  [Network.STELLAR]: -5,
  [Network.ETHEREUM]: CHAIN_ID.ETHEREUM,
};

const api = axios.create({
  baseURL: config.apiURL,
});

const apiV2 = axios.create({
  baseURL: config.apiURL,
});

const buildUrl = (base: string, parts: Array<string | number | undefined | null>): string => {
  return [base, ...parts.filter((part) => part !== undefined && part !== null)].join("/");
};

class HapiService {
  async getRisk({ address }: { address: string }) {
    return api.get<RiskResponse>(`${API_ROUTES.RISK}/${address}`);
  }

  async getTotalScore({ tgId, accountId }: { tgId: number; accountId: string }) {
    return api.post<TotalScoreResponse>(`${API_ROUTES.TOTAL_SCORE}`, {
      tg_id: tgId,
      account_id: accountId,
    });
  }

  async getTrust({
    network,
    nearAddress,
    address,
    chainId,
    tg,
  }: {
    network: Network;
    nearAddress: string | null;
    address: string | undefined;
    chainId: number | null;
    tg: number | null | undefined;
  }) {
    try {
      if (apiVersion === ApiVersion.v1 && nearAddress) {
        return this.getScoreV1({ network, nearAddress, address, chainId, tg });
      } else {
        return this.getScoreV2({ network, address });
      }
    } catch (e) {
      return { errorCode: 1, message: String(e) };
    }
  }

  async getUser({
    nearAccount,
    evmAccount,
    solanaAccount,
    tonAccount,
    stellarAccount,
    tg,
  }: {
    nearAccount: string | null;
    evmAccount: string | null;
    solanaAccount: string | null | undefined;
    tonAccount: string | null | undefined;
    stellarAccount: string | null | undefined;
    tg: number | null | undefined;
  }): Promise<UserResponse> {
    try {
      if (apiVersion === ApiVersion.v1) {
        const data = await api.post<UserResponse>(API_ROUTES.USER, {
          near: nearAccount,
          evm: evmAccount || undefined,
          solana: solanaAccount || undefined,
          ton: tonAccount || undefined,
          stellar: stellarAccount || undefined,
          tg: tg ? `${tg}` : undefined,
        });
        return data.data;
      }
      const jwtToken = localStorage.getItem(localStorageKey);
      if (!jwtToken) return { errorCode: 1, message: "No jwtToken provided" };
      const dataV2 = await apiV2.get<UserResponse>(API_ROUTES_V2.USER, {
        headers: { Authorization: `Bearer ${jwtToken}` },
      });
      return dataV2.data;
    } catch (e) {
      return { errorCode: 1, message: String(e) };
    }
  }

  async getMint({ address, transaction, chainId }: { address: string; chainId?: number | null; transaction?: string }) {
    const baseURL = buildUrl(API_ROUTES.MINT, [address, chainId, transaction]);
    return api.get<MintResponse>(baseURL);
  }

  async getUpdateMint({
    address,
    transaction,
    chainId,
  }: {
    address: string;
    chainId?: number | null;
    transaction?: string;
  }) {
    const baseURL = buildUrl(API_ROUTES.UPDATE_MINT, [address, chainId, transaction]);
    return api.get<MintResponse>(baseURL);
  }

  async readNews({ address }: { address: string }) {
    const baseURL = buildUrl(API_ROUTES.READ_NEWS, [address]);
    return api.get(baseURL);
  }

  async connectTwitter({ address }: { address: string }) {
    const baseURL = buildUrl(API_ROUTES.CONNECT_TWITTER, [address]);
    const response = await api.get<XResponse>(baseURL);
    return "url" in response.data ? response.data.url : null;
  }

  async connectTelegram({ address, telegramId }: { address: string; telegramId: number }) {
    const baseURL = buildUrl(API_ROUTES.CONNECT_TELEGRAM, [address, telegramId]);
    return api.get<BasicResponse>(baseURL);
  }

  async logAttest({
    address,
    chain,
    method,
  }: {
    address: string;
    chain: number;
    method: 0 | 1; //method - 0 attest | method - 1 replace
  }) {
    return api.post(API_ROUTES.LOG_ATTEST, { address, chain, method });
  }

  async logError({ nearAddress, action, error }: { nearAddress: string | null; action: ErrorAction; error: unknown }) {
    let value: string = action.toLowerCase();
    if (action in API_ROUTES) {
      value = API_ROUTES[action as keyof typeof API_ROUTES];
    }
    const jsonError = JSON.stringify(error, Object.getOwnPropertyNames(error));

    return api.post(API_ROUTES.LOG_ERROR, {
      nearAddress,
      errorBody: JSON.stringify({ action: value, error: jsonError }),
    });
  }

  async getAirdropSignature({ address, nftIds }: { address: string; nftIds: Array<string> }) {
    return api.post<AirdropSignatureResponse>(API_ROUTES.AIRDROP, {
      message: `${nftIds.join(",")},${address}`,
    });
  }

  async getScoreV1({
    network,
    nearAddress,
    address,
    chainId,
    tg,
  }: {
    network: Network;
    nearAddress: string;
    address: string | undefined;
    chainId: number | null;
    tg: number | null | undefined;
  }): Promise<TrustResponse> {
    const { data } = await api.post<TrustResponse>(API_ROUTES.TRUST, {
      network,
      nearAddress,
      address: address,
      chainId: chainId || undefined,
      tg: tg ? `${tg}` : undefined,
    });
    return { ...data };
  }

  async getScoreV2({ network, address }: { network: Network; address?: string }): Promise<TrustResponse> {
    const jwtToken = localStorage.getItem(localStorageKey);

    switch (network) {
      case Network.TON_TESTNET:
      case Network.TON: {
        const { data } = await apiV2.post<BasicResponseV2Score>(
          API_ROUTES_V2.TRUST,
          { address, network: NetworkChainMap[network] },
          { headers: { Authorization: `Bearer ${jwtToken}` } },
        );
        return {
          errorCode: 0,
          wallet: address || "",
          validUntil: data.expiration,
          signature: data.signature,
          trust: data.score,
          expiration: data.expiration,
        };
      }
      default:
        throw Error("No matching network handler provided");
    }
  }

  async getMessage() {
    return apiV2.get<BasicResponseV2>(API_ROUTES_V2.TON_PAYLOAD);
  }

  async getGraph(network: Network, userId: string) {
    return await api.post<GraphResponse>(API_ROUTES.GET_GRAPH, {
      account_id: userId,
      chain_id: INTERNAL_CHAIN_BY_NETWORK[network],
    });
  }

  async checkProof({
    proof,
    address,
    network,
  }: {
    proof: {
      state_init: string;
      timestamp: number;
      domain: { lengthBytes: number; value: string };
      payload: string;
      signature: string;
    };
    address: string;
    network: number;
  }) {
    return apiV2.post<BasicResponseV2Auth>(API_ROUTES_V2.TON_LOGIN, { proof, address, network });
  }
}

export const hapiApi = new HapiService();
